<template>
	<div class="image-gallery">
		<button
			class="image-gallery__button"
			@click="handleButtonClick"
			@keyup.enter="handleButtonClick"
		>
			<BaseIcon
				class="image-gallery__icon"
				name="gallery"
			/>
			{{ images.length }}
		</button>

		<GalleryModalWithGrid
			ref="gridModalRef"
			:header-title="hotelName"
			:focused-index="modalIndex"
			:gallery-items="actualImages"
		/>
	</div>
</template>

<script setup lang="ts">
import BaseIcon from '@lmt-rpb/BaseIcon/BaseIcon.vue';
import { ref, computed, useTemplateRef } from 'vue';
import { useGalleryListeners } from '../../composables/galleryListeners';
import type { GalleryElement } from '@/interfaces/components/galleryElement';
import SelectContentAnalytics from '@/js/modules/analytics/SelectContentAnalytics';
import GalleryModalWithGrid from '@lmt-rpb-new/molecules/GalleryModalNew/GalleryModalWithGrid.vue';

export type FluxImageData = {
	small: string;
	thumb: string;
	original: string;
	preview: string;
	srcset: string;
};

type Props = {
	images: Array<FluxImageData>;
	hotelName?: string;
};

const props = defineProps<Props>();

const actualImages = computed<GalleryElement[]>(() =>
	props.images.map((image, index) => ({
		content: image.original,
		preview: image.preview,
		type: 'image',
		copyright: `© GIATA GmbH 1996 - ${new Date().getFullYear()}`,
		alt: `Image ${index + 1} of ${props.hotelName}`,
	}))
);

const modalIndex = ref(0);
const gridModalRef = useTemplateRef('gridModalRef');

const handleButtonClick = () => {
	SelectContentAnalytics.selectContent({ cId: 'hotel_gallery_button' });
	openGallery(0);
};

const handleImageClick = (index: number) => {
	SelectContentAnalytics.selectContent({ cId: 'hotel_gallery_item', index: index + 1 });
	openGallery(index);
};

const openGallery = (index: number) => {
	modalIndex.value = index;
	if (gridModalRef.value) {
		gridModalRef.value.openGalleryGridModal();
	}
};

useGalleryListeners('.hotel-gallery__item', handleImageClick);
</script>

<style lang="scss" scoped>
.image-gallery {
	&__button {
		appearance: none;
		border: none;
		position: absolute;
		bottom: 7.8rem;
		right: 0.8rem;
		display: flex;
		align-items: center;
		justify-content: center;
		column-gap: 0.8rem;
		padding: 0.8rem 1.6rem;
		background-color: #000000a6;
		color: white;
		border-radius: $border-radius-button;
		z-index: 1;
		font-size: $font-small-2;
		font-weight: $font-weight-bold;

		@media screen and (min-width: $breakpoint-small) {
			bottom: 0.8rem;
		}
	}

	&__icon {
		width: 2.7rem;
		height: 2.2rem;
	}
}

@media screen and (min-width: $breakpoint-small) {
	.image-gallery {
		&__button {
			right: 2.4rem;
		}
	}
}

@media screen and (min-width: $breakpoint-large) {
	.image-gallery {
		&__button {
			bottom: 2.4rem;
			right: 3.5rem;
			width: 9rem;

			// TODO:
			font-size: $font-small-1;
			font-weight: $font-weight-bold;
		}

		&__icon {
			width: 3rem;
			height: 2.5rem;
		}
	}
}
</style>
